/**
 * NFC Solutions — Snapshot Refresh Loop.
 *
 * Session 27. Complements `eventSync.js`:
 *   • eventSync = outbound: writes → cloud
 *   • snapshotRefresh = inbound: cloud → local DB (periodic)
 *
 * Why it exists
 * -------------
 * The "true offline startup" architecture requires the client to be
 * the source of truth for reads. That means when a user is
 * **deactivated** or their **PIN is reset** in the cloud, the change
 * has to make its way onto the device and REVOKE offline access.
 *
 * Realtime WebSocket events already handle many mutations, but a
 * belt-and-braces periodic snapshot refresh is the simplest way to
 * catch:
 *   - user deactivation
 *   - user PIN reset (`pin_hash` change)
 *   - user addition
 *   - business rename / config changes
 *
 * Rules
 * -----
 *   • Never blocks boot — first refresh runs ~30s after arming.
 *   • Only runs when online AND an auth token is available.
 *   • Silent — logs failures to console but never surfaces to UI.
 *   • Purges offline credentials for users no longer active locally.
 */

import axios from "axios";
import {
  hydrateFromSnapshot,
  readAll,
  clearOfflineCredentials,
  listOfflineCredentialUserIds,
} from "./localDb";

// 15 minutes — frequent enough that revocations propagate quickly,
// infrequent enough that we don't waste bandwidth on medium-size
// snapshots (typical restaurant: 100–300 KB).
const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
// First refresh delay from arming — kept short so any operator who
// signs in and closes the tab quickly still ends up with a full local
// mirror of the business. Session 27.10 dropped this from 30s → 3s
// because production users on flaky Wi-Fi were losing the initial
// snapshot fetch when the tab was backgrounded before the loop fired.
const INITIAL_DELAY_MS    = 3 * 1000;

let _armed = false;
let _refreshing = false;
let _timer = null;
let _tokenSupplier = null;
let _onRefreshed = null;

function _backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
}

/** Configure the loop — same shape as configureEventSync so the two
 * can share a token supplier. `onRefreshed` is an optional callback
 * invoked after a successful refresh (used by settings UI to
 * re-render). */
export function configureSnapshotRefresh({ tokenSupplier, onRefreshed } = {}) {
  _tokenSupplier = typeof tokenSupplier === "function" ? tokenSupplier : null;
  _onRefreshed   = typeof onRefreshed   === "function" ? onRefreshed   : null;
}

async function _revokeInactiveUsers() {
  // After the fresh snapshot has been hydrated, any user marked
  // `active=false` (or removed entirely) must lose offline access.
  try {
    const [users, offlineIds] = await Promise.all([
      readAll("users"),
      listOfflineCredentialUserIds(),
    ]);
    const active = new Set(
      (users || [])
        .filter((u) => u && u.active !== false)
        .map((u) => String(u.id)),
    );
    for (const id of offlineIds || []) {
      if (!active.has(String(id))) {
        try { await clearOfflineCredentials(id); }
        catch (e) { console.warn("[snapshot-refresh] clearOfflineCredentials error:", e?.message || e); }
      }
    }
  } catch (_) { /* user roster read best-effort */ }
}

async function _refreshOnce() {
  if (_refreshing) return;
  _refreshing = true;
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const token = _tokenSupplier ? _tokenSupplier() : null;
    if (!token) return;
    // Offline session tokens live client-side only — using them here
    // would just yield a 401.
    if (String(token).startsWith("offline:")) return;

    const url = `${_backendUrl()}/api/sync/snapshot`;
    let res = null;
    try {
      res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
    } catch (err) {
      // 401/403 — the token expired; downstream role login will
      // handle re-auth. Silent for the rest.
      return;
    }
    const snapshot = res && res.data;
    if (!snapshot || typeof snapshot !== "object") return;
    await hydrateFromSnapshot(snapshot);
    await _revokeInactiveUsers();
    if (_onRefreshed) {
      try { _onRefreshed(snapshot); } catch (e) { console.warn("[snapshot-refresh] onRefreshed callback error:", e?.message || e); }
    }
    // Broadcast a DOM event so any interested component (settings,
    // login screen) can re-read fresh data without polling.
    try {
      window.dispatchEvent(new CustomEvent("nfcs:snapshot-refreshed", {
        detail: { server_time: snapshot.server_time || null },
      }));
    } catch (_) { /* dispatchEvent best-effort */ }
  } catch (err) {
    // Never throw out of the loop.
    try { console.warn("[snapshot-refresh] failed:", err && err.message || err); }
    catch (_) { /* noop */ }
  } finally {
    _refreshing = false;
  }
}

function _armInterval() {
  if (_timer) return;
  _timer = setInterval(_refreshOnce, REFRESH_INTERVAL_MS);
}

/** Arm the snapshot refresh loop. Idempotent. Non-blocking. */
export function armSnapshotRefresh() {
  if (_armed) return;
  if (typeof window === "undefined") return;
  _armed = true;

  // Refresh when connectivity returns (e.g. after airplane mode off).
  window.addEventListener("online", () => setTimeout(_refreshOnce, 500));
  // Refresh right after any local event syncs — useful after login
  // when the sync loop first activates.
  window.addEventListener("nfcs:sync-succeeded", () => setTimeout(_refreshOnce, 200));

  _armInterval();
  // Kick off after a delay so boot hydration wins.
  setTimeout(_refreshOnce, INITIAL_DELAY_MS);
}

/** Manual refresh — used by Settings' "Re-verify" button. */
// Session 30.34 — remember when the local snapshot was last refreshed so
// the post-login "Updating…" step can be skipped entirely when the data is
// already fresh (e.g. right after the install wizard just downloaded it).
export const SNAPSHOT_FRESH_KEY = "nfcs:snapshot:last-refresh-at";
export function markSnapshotFresh() {
  try { window.localStorage.setItem(SNAPSHOT_FRESH_KEY, String(Date.now())); } catch (_) { /* noop */ }
}
export function snapshotFreshWithin(ms) {
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_FRESH_KEY);
    const at = raw ? Number(raw) : 0;
    return at > 0 && (Date.now() - at) < ms;
  } catch (_) { return false; }
}

export async function refreshSnapshotNow() {
  await _refreshOnce();
  markSnapshotFresh();
}
